import {useCallback, useEffect, useMemo} from "react";
import {useRealtime} from "@/api/realtime/RealtimeHooks.ts";

export function useRealtimeChannel<TEvent, TCommand = TEvent>(topic: string) {
    const realtime = useRealtime();
    const subscribe = useCallback(
        (listener: (event: TEvent) => void) => realtime.subscribe(topic, listener),
        [realtime, topic],
    );
    const publish = useCallback(
        (command: TCommand) => realtime.publish(topic, command),
        [realtime, topic],
    );
    const onConnected = useCallback(
        (listener: () => void) => realtime.onConnected(listener),
        [realtime],
    );

    return useMemo(() => ({subscribe, publish, onConnected}), [onConnected, publish, subscribe]);
}

export function useRealtimeSubscription<T>(
    topic: string | undefined,
    listener: (event: T) => void,
): void {
    const realtime = useRealtime();
    useEffect(() => {
        if (!topic) return;
        return realtime.subscribe(topic, listener);
    }, [listener, realtime, topic]);
}

export function useRealtimePublisher<T>(destination: string) {
    const realtime = useRealtime();
    return useCallback((message: T) => realtime.publish(destination, message), [destination, realtime]);
}
